import type { PropsWithChildren } from "react";
import { match } from "ts-pattern";

import { createZustandContext } from "@/lib/store/zustand-context-pattern";

import type { CFormEditUnit, CFormItemLayoutType } from "./cform-types";

type CFormProps = PropsWithChildren<{
  editUnit: CFormEditUnit;
  itemLayout?: CFormItemLayoutType;
  isPending?: boolean;
  onSave?: () => Promise<unknown>;
  onReset?: () => void;
}>;

export type CFormStore = {
  state: {
    editUnit: CFormEditUnit;
    itemLayout: CFormItemLayoutType;
    isEditingAll: boolean;
    editingSectionId?: string;
    editingItemId?: string;
    isPending: boolean;
    _originalOnSave?: () => Promise<unknown>;
    _originalOnReset?: () => void;

    isHideSectionActions: (p: { sectionId: string }) => boolean;
    isHideItemActions: (p: { itemId: string }) => boolean;
    isSectionEditing: (p: { sectionId: string }) => boolean;
    isItemEditing: (p: { sectionId?: string; itemId: string }) => boolean;
  };
  actions: {
    init: (props: CFormProps) => void;
    startEditSection: (sectionId: string) => void;
    startEditItem: (itemId: string) => void;
    save: () => void;
    reset: () => void;
  };
};

export const { context: CFormStore, useStore: useCFormStore } =
  createZustandContext<CFormStore>((set, get) => ({
    state: {
      editUnit: "all",
      itemLayout: "card",
      isEditingAll: false,
      editingSectionId: undefined,
      editingItemId: undefined,
      isPending: false,
      _originalOnSave: undefined,
      _originalOnReset: undefined,

      /** 特定の Section のアクションを非表示にすべきか判断する */
      isHideSectionActions: (p: { sectionId: string }): boolean => {
        const { state } = get();
        const hideSectionActions = match(state)
          .with({ editUnit: "all" }, () => false)
          .with(
            { editUnit: "section" },
            () =>
              state.editingSectionId != null &&
              state.editingSectionId !== p.sectionId,
          )
          .with({ editUnit: "item" }, () => true)
          .otherwise(() => false);
        return hideSectionActions;
      },
      /** 特定の Item のアクションを非表示にすべきか判断する */
      isHideItemActions: (p: { itemId: string }): boolean => {
        const { state } = get();

        const hideItemActions = match(state)
          .with({ editUnit: "all" }, () => true)
          .with({ editUnit: "section" }, () => true)
          .with(
            { editUnit: "item" },
            // 他のアイテムを編集中なら隠す
            (state) =>
              state.editingItemId != null && state.editingItemId !== p.itemId,
          )
          .otherwise(() => true);
        return hideItemActions;
      },
      /** 特定の Section が編集状態であるか判断する */
      isSectionEditing: (p: { sectionId: string }): boolean => {
        const { state } = get();
        const isEditingItem = match(state)
          .with({ editUnit: "all" }, (state) => state.isEditingAll)
          .with(
            { editUnit: "section" },
            (state) =>
              state.editingSectionId != null &&
              state.editingSectionId === p.sectionId,
          )
          .with({ editUnit: "item" }, () => false)
          .otherwise(() => false);
        return isEditingItem;
      },
      /** 特定の Item が編集状態であるか判断する */
      isItemEditing: (p: { sectionId?: string; itemId: string }): boolean => {
        const { state } = get();
        const isEditingItem = match(state)
          .with({ editUnit: "all" }, (state) => state.isEditingAll)
          .with(
            { editUnit: "section" },
            (state) =>
              state.editingSectionId != null &&
              state.editingSectionId === p.sectionId,
          )
          .with(
            { editUnit: "item" },
            (state) => state.editingItemId === p.itemId,
          )
          .otherwise(() => false);
        return isEditingItem;
      },
    },
    actions: {
      init: (props: CFormProps) => {
        set((store) => {
          store.state.editUnit = props.editUnit;
          store.state.itemLayout = props.itemLayout ?? "card";
          store.state._originalOnSave = props.onSave ?? (async () => {});
          store.state._originalOnReset = props.onReset ?? (() => {});
        });
      },
      startEditSection: (id: string) => {
        const editUnit = get().state.editUnit;
        if (editUnit !== "all" && editUnit !== "section") {
          throw new Error("editUnit should be all | section");
        }
        const isEditAll = editUnit === "all";
        set((store) => {
          store.state.isEditingAll = isEditAll;
          store.state.editingSectionId = isEditAll ? undefined : id;
          store.state.editingItemId = undefined;
        });
      },
      startEditItem: (itemId: string) => {
        const editUnit = get().state.editUnit;
        if (editUnit !== "item") {
          throw new Error("editUnit should be item");
        }
        set((store) => {
          store.state.isEditingAll = false;
          store.state.editingSectionId = undefined;
          store.state.editingItemId = itemId;
        });
      },
      save: async () => {
        const { state, actions } = get();
        set((store) => {
          store.state.isPending = true;
        });
        try {
          await state._originalOnSave?.();
          actions.reset();
        } finally {
          set((store) => {
            store.state.isPending = false;
          });
        }
      },
      reset: () => {
        const { state } = get();
        state._originalOnReset?.();
        set((store) => {
          store.state.isEditingAll = false;
          store.state.editingSectionId = undefined;
          store.state.editingItemId = undefined;
        });
      },
    },
  }));
